import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import type { TypeParams } from '../../types'

import { getActionsConfig } from './actions'
import * as effects from './effects'
import * as extraReducers from './extra-reducers'
import { Selectors } from './selectors'
import { State } from './state'

/**
 * Detects duplicate employees based on name and job title during employee
 * creation.
 *
 * Automatically queries for existing employees matching the name and job title
 * being entered, debounced to avoid excessive API calls. Clears results when
 * the form is submitted or discarded.
 */
export class Plugin extends ec.plugins.Plugin<
  TypeParams,
  typeof Plugin.id,
  State,
  Selectors,
  ReturnType<typeof getActionsConfig>
> {
  /** @see {@link ec.plugins.Plugin.Id.Custom} */
  public static id = ec.plugins.Plugin.Id.Custom.create('DuplicateDetection')

  constructor({ collection }: Readonly<Plugin.constructor.Params>) {
    super({
      actions: getActionsConfig(),
      collection,
      id: Plugin.id,
      initialState: State.INITIAL,
      selectors: () => new Selectors(this),
    })

    this.feature.registerExtraReducers(
      extraReducers.resetOnContextChange(this),
      extraReducers.resetOnEditingComplete(this),
      extraReducers.updateCheckingOnDuplicateSearchEdit(this),
    )

    this.feature.registerEffects(
      effects.executeLoad(this),
      effects.loadForCurrentEntity(this),
      effects.loadForCurrentEntityOnContextChange(this),
      effects.loadForCurrentEntityOnEditedEntityChange(this),
      effects.selectDuplicates(collection),
    )
  }
}

export namespace Plugin {
  export namespace constructor {
    export interface Params {
      collection: ec.Collection<TypeParams>
    }
  }
}
